import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import PersonIcon from "@mui/icons-material/Person";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { CircularProgress } from "@mui/material";
import { LuPackage } from "react-icons/lu";
import { useDispatch } from "react-redux";
import { convertToLocalDateTime } from "../../product/all-products/Middleware/commonUtils";
import { setBoardItem } from "../../product/all-products/redux/Actions/paginationActions";
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import swiggy from "../../../../assets/images/swiggy.png";
import zomato from "../../../../assets/images/zomato.png";
import uberEats from "../../../../assets/images/uberEats.jpg";
import dineIn from "../../../../assets/images/dineIn.jpg";
import whatsapp from "../../../../assets/images/whatsapp.png";
import cutlery from "../../../../assets/images/cutlery.png";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { constants } from "buffer";
import PosCustomIcon from "../../../../customComponents/PosCustomIcon/PosCustomIcon";
import {
  BellSlashIcon,
  ChevronRightIcon,
  ClockIcon,
  PrinterIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { getOrderDetails } from "../../../../config/Services";
import scooterIcon from "../../../../assets/images/ods-scooter.png";
import cubeIcon from "../../../../assets/images/cube-takeaway.png";
import { PosCustomButton } from "../../../../customComponents/PosCustomButton/PosCustomButton";
import LogisticsModal from "./LogisticsModal";
import TableConfirmationModal from "../../table-layout/components/TableConfirmationModal/TableConfirmationModal";
import { stopOrderAlert } from "../../../../utils/notificationSystem/orderAlertAudio";
import LabelPrintModal from "./LabelPrintModal/LabelPrintModal";
import { getStoreCurrencySymbol } from "../../../../utils/currency";
import "./BoardItem.css";

const ArrowIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" height="15" width="15">
    <path
      fill="#fff"
      d="M13.4697 17.9697C13.1768 18.2626 13.1768 18.7374 13.4697 19.0303C13.7626 19.3232 14.2374 19.3232 14.5303 19.0303L20.3232 13.2374C21.0066 12.554 21.0066 11.446 20.3232 10.7626L14.5303 4.96967C14.2374 4.67678 13.7626 4.67678 13.4697 4.96967C13.1768 5.26256 13.1768 5.73744 13.4697 6.03033L18.6893 11.25H4C3.58579 11.25 3.25 11.5858 3.25 12C3.25 12.4142 3.58579 12.75 4 12.75H18.6893L13.4697 17.9697Z"
    ></path>
  </svg>
);

const BoardItem = ({
  item,
  isLoading,
  setShouldRefresh,
  onCancelOrder,
  cancelLoading,
  onPrintLabels,
  isFirstCreatedCard,
  onAcceptOrder,
}) => {
  const [isLogisticsModalOpen, setIsLogisticsModalOpen] = useState(false);
  const [tableConfirmationModalOpen, setTableConfirmationModalOpen] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [isFetchingItems, setIsFetchingItems] = useState(false);
  const [isLabelModalOpen, setIsLabelModalOpen] = useState(false);
  const [labelItems, setLabelItems] = useState([]);
  const [isPrintingLabels, setIsPrintingLabels] = useState(false);

  const dispatch = useDispatch();
  const retailType = localStorage.getItem("retailType");

  const handleItem = (data) => {
    dispatch(setBoardItem(data));
    setShouldRefresh(false);
  };

  const timeRemaining = useMemo(() => {
    const endTime = moment.utc(item?.endTime).local();
    const currentTime = moment();
    const diff = endTime.diff(currentTime, "minutes");
    return diff;
  }, [item?.endTime]);

  const timeRemainingText = useMemo(() => {
    if (timeRemaining < 0) {
      return "Expired";
    }
    const hours = Math.floor(timeRemaining / 60);
    const minutes = timeRemaining % 60;
    if (hours > 0) {
      return `${hours} hrs ${minutes} mins left`;
    }
    return `${minutes} mins left`;
  }, [timeRemaining]);

  // Calculate time difference between ACCEPTED and PREPARING
  const stateDifference = useMemo(() => {
    // Check if the order is in PREPARING state and we have both timestamps
    if (item?.originalStatus === "PREPARING" && item?.updatedAt && item?.createdAt) {
      // Use updatedAt as the time when it moved to PREPARING
      // Use createdAt or acceptedAt as the base time
      const baseTime = moment(item?.acceptedAt || item?.createdAt);
      const preparingTime = moment(item?.updatedAt);
      const diff = preparingTime.diff(baseTime, "minutes");

      if (diff >= 0) {
        const hours = Math.floor(diff / 60);
        const minutes = diff % 60;
        if (hours > 0) {
          return `${hours}h ${minutes}m in preparing`;
        }
        return `${minutes}m in preparing`;
      }
    }

    // If in ACCEPTED state, show how long it's been accepted
    if (item?.fulfilmentStatus === "ACCEPTED" && !item?.originalStatus) {
      const acceptedTime = moment(item?.acceptedAt || item?.updatedAt || item?.createdAt);
      const currentTime = moment();
      const diff = currentTime.diff(acceptedTime, "minutes");

      if (diff >= 0) {
        const hours = Math.floor(diff / 60);
        const minutes = diff % 60;
        if (hours > 0) {
          return `${hours}h ${minutes}m since accepted`;
        }
        return `${minutes}m since accepted`;
      }
    }

    return null;
  }, [
    item?.originalStatus,
    item?.fulfilmentStatus,
    item?.acceptedAt,
    item?.createdAt,
    item?.updatedAt,
  ]);

  // Determine the display status badge
  const displayStatus = useMemo(() => {
    if (item?.originalStatus === "PREPARING") {
      return {
        label: "Preparing",
        color: "#FF6B35",
        bgColor: "#FFF3E0",
        borderColor: "#FF6B35",
      };
    } else if (item?.fulfilmentStatus === "ACCEPTED") {
      return {
        label: "Accepted",
        color: "#D4BD19",
        bgColor: "#FFFDE7",
        borderColor: "#D4BD19",
      };
    }
    return null;
  }, [item?.originalStatus, item?.fulfilmentStatus]);

  const formatSalesChannel = (channel) => {
    if (!channel) return "";

    const channelMap = {
      SWIGGY_ORDER: {
        label: "Swiggy",
        color: "#FFFFFF",
        backgroundColor: "#FF6900",
      },
      ZOMATO_ORDER: {
        label: "Zomato",
        color: "#FFFFFF",
        backgroundColor: "#E7000B",
      },
      UBER_EATS: {
        label: "Dine In",
        color: "#FFFFFF",
        backgroundColor: "#00A63E",
      },
      RESTAURANT_ORDER: {
        label: "Restaurant",
        color: "#FFFFFF",
        backgroundColor: "#1F63EA",
      },
      DELIVERY: {
        label: "Delivery",
        color: "#FFFFFF",
        backgroundColor: "#1F63EA",
      },
      POS: {
        label: "POS",
        color: "#FFFFFF",
        backgroundColor: "#1F63EA",
      },
      ONLINE: {
        label: "Online",
        color: "#FFFFFF",
        backgroundColor: "#1F63EA",
      },
      WHATSAPP_ORDER: {
        label: "Whatsapp",
        color: "#FFFFFF",
        backgroundColor: "#4CAF50",
      },
      B2C_ORDER: {
        label: "Website",
        color: "#FFFFFF",
        backgroundColor: "#1F63EA",
      },
    };

    if (channelMap[channel]) {
      return channelMap[channel];
    }

    const formatted = channel.replace(/_/g, " ").toLowerCase();
    return {
      label: formatted.charAt(0).toUpperCase() + formatted.slice(1),
      color: "#757575",
    };
  };

  const salesChannelInfo = formatSalesChannel(item?.salesChannel);

  const getRiderStatus = (status) => {
    if (!status) {
      return {
        label: "Not Assigned",
        color: "#87125A",
        legend: "#87125A",
        backgroundColor: "#FFDDF2",
      };
    }

    const statusMap = {
      SCHEDULED: {
        label: "Scheduled",
        color: "#0860e6",
        legend: "#0860e6",
        backgroundColor: "#edf8ff",
      },

      ASSIGNMENT_IN_PROGRESS: {
        label: "Searching",
        color: "#414651",
        legend: "#414651",
        backgroundColor: "#FFFEE7",
      },

      CANCELLED: {
        label: "Canceled",
        color: "#E90000",
        legend: "#E90000",
        backgroundColor: "#FFC8C5",
      },

      ASSIGNED: {
        label: "Assigned",
        color: "#0860e6",
        legend: "#0860e6",
        backgroundColor: "#edf8ff",
      },

      UNASSIGNED: {
        label: "Not Assigned",
        color: "#87125A",
        legend: "#87125A",
        backgroundColor: "#FFDDF2",
      },

      REASSIGNED: {
        label: "Rider Changed",
        color: "#414651",
        legend: "#414651",
        backgroundColor: "#FFF5B2",
      },

      AT_STORE: {
        label: "Arrived",
        color: "#BA623C",
        legend: "#BA623C",
        backgroundColor: "#F6EBDE",
      },

      IN_TRANSIT: {
        label: "Out For Delivery",
        color: "#414651",
        legend: "#414651",
        backgroundColor: "#E9EAEB",
      },

      DELIVERED: {
        label: "Delivered",
        color: "#095120",
        legend: "#095120",
        backgroundColor: "#BFE6A3",
      },

      ADDITIONAL_RIDER_ASSIGNED: {
        label: "Additional Rider",
        color: "#0860e6",
        legend: "#0860e6",
        backgroundColor: "#edf8ff",
      },
    };

    if (statusMap[status]) {
      return statusMap[status];
    }

    return {
      label: "Not Assigned",
      color: "#87125A",
      legend: "#87125A",
      backgroundColor: "#FFDDF2",
    };
  };

  const riderStatusInfo = getRiderStatus(item?.riderCurrentStatus);

  const extractTime = (utcTime) => {
    if (!utcTime) return "";

    const date = new Date(utcTime);

    let hours = date.getHours();
    let minutes = date.getMinutes();

    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    minutes = minutes.toString().padStart(2, "0");

    return `${hours}:${minutes} ${ampm}`;
  };

  const captializeFirstLetter = (text) => {
    if (!text) return "NA";
    const capital = text?.charAt(0).toUpperCase() + text?.slice(1).toLowerCase();
    return capital;
  };

  const getRiderChannelColor = (channel) => {
    if (!channel) return "#5b6072";

    switch (channel?.toLowerCase()) {
      case "zomato":
        return "#ED2015";
      case "swiggy":
        return "#F6881F";
      default:
        return "#5b6072";
    }
  };

  const timeConverter = (minutes) => {
    if (!minutes) return `0 mins`;
    if (minutes < 60) return `${minutes}mins`;
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs}hrs ${mins}mins`;
  };

  const handleLogisticsAssignment = () => {
    setIsLogisticsModalOpen(true);
  };

  const showLogisticButton = () => {
    if (
      item?.assignmentMode ||
      item?.assignmentMode === "true" ||
      item?.assignmentMode === "TRUE"
    ) {
      return true;
    }
    return false;
  };

  const isShowLogisticButton = showLogisticButton();

  const handleRejectClick = () => {
    const status = item?.fulfilmentStatus;

    if (status === "CREATED") {
      onCancelOrder(item?.orderId);
      return;
    }

    if (status === "ACCEPTED") {
      setConfirmationText("Order is already Accepted do you want to reject it");
      setTableConfirmationModalOpen(true);
      return;
    }

    if (status === "PACKAGED") {
      setConfirmationText("Order is Ready do you want to reject it");
      setTableConfirmationModalOpen(true);
      return;
    }
  };

  const handleConfirmReject = () => {
    setTableConfirmationModalOpen(false);
    onCancelOrder(item?.orderId);
  };

  const showRejectOrderButton = () => {
    if (retailType === "RESTAURANT") {
      if (item?.fulfilmentStatus === "CREATED") {
        return true;
      }
    }
    return false;
  };

  const isShowRejectOrderButton = showRejectOrderButton();

  const handleStopBuzzer = () => {
    stopOrderAlert();
  };

  const appConfig = JSON.parse(localStorage.getItem("appConfig") || "{}");
  const isLabelPrinterEnabled =
    retailType !== "GROCERY" && appConfig?.all?.LABEL_PRINTER?.flag === true;

  const handleOpenLabelModal = async (e) => {
    e.stopPropagation();
    if (!onPrintLabels || isFetchingItems) return;
    setIsFetchingItems(true);
    try {
      const locId = localStorage.getItem("locId");
      const res = await getOrderDetails(item?.orderId, locId);
      const items = res?.data?.data?.baseOrderResponse?.orderItemList || [];
      setLabelItems(items);
      setIsLabelModalOpen(true);
    } catch {
      // silent fail
    } finally {
      setIsFetchingItems(false);
    }
  };

  const handleLabelPrint = async ({
    items,
    customerName,
    token,
    labelWidthMm,
    labelHeightMm,
    gapMm,
    labelsPerRow,
  }) => {
    setIsPrintingLabels(true);
    try {
      await onPrintLabels({
        items,
        customerName,
        token,
        labelWidthMm,
        labelHeightMm,
        gapMm,
        labelsPerRow,
      });
      setIsLabelModalOpen(false);
    } catch {
      // printer error is surfaced by usePrint
    } finally {
      setIsPrintingLabels(false);
    }
  };

  const getSubOrderType = (subOrderType) => {
    const subOrderMap = {
      DELIVERY: {
        label: "DELIVERY",
        color: "#7918E9",
        backgroundColor: "#F5F3FF",
        borderColor: "#EDE9FE",
      },
      WHATSAPP_DINE_IN: {
        label: "DINE IN",
        color: "#FFFFFF",
        backgroundColor: "#00A63E",
        borderColor: "var(--surface-g0)",
      },
      TAKE_AWAY: {
        label: "TAKEAWAY",
        color: "#BB4D00",
        backgroundColor: "#FFFBEB",
        borderColor: "#FEF3C6",
      },
    };

    return subOrderMap[subOrderType] || { label: "N/A" };
  };

  const showSubOrderType = getSubOrderType(item?.subOrderType);

  return (
    <div className="ods-board-item-box">
      {isLoading && (
        <div className="board-loader">
          <CircularProgress size={26} thickness={5} color="inherit" />
        </div>
      )}

      <div className="ods-board-item-card-header-container">
        <div className="ods-board-item-header">
          <div className="ods-board-time-container">
            {/* Time */}
            <div className="ods-board-item-time">
              <PosCustomIcon icon={<ClockIcon />} height={"12px"} width={"12px"} color="#90A1B9" />
              <p>{extractTime(item?.createdAt)}</p>
            </div>

            {/* Bell and Print */}
            <div className="ods-board-bell-container">
              {item?.fulfilmentStatus === "CREATED" && isFirstCreatedCard && (
                <div className="ods-board-bell">
                  <PosCustomIcon
                    icon={<BellSlashIcon />}
                    color="var(--red-solid)"
                    height={"20px"}
                    width={"20px"}
                    pointer={true}
                    onClickFunction={handleStopBuzzer}
                  />
                </div>
              )}
              {onPrintLabels && isLabelPrinterEnabled && (
                <button
                  className="ods-label-print-btn"
                  onClick={handleOpenLabelModal}
                  disabled={isFetchingItems}
                  title="Print item labels"
                >
                  {isFetchingItems ? (
                    <span style={{ fontSize: 9, lineHeight: 1 }}>…</span>
                  ) : (
                    <PrinterIcon style={{ width: 13, height: 13 }} />
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Order Id */}
          <div className="ods-board-order-id">
            Order Id: {item?.externalOrderId ? item?.externalOrderId : item?.orderId}
          </div>

          <div className="ods-board-item-kot-container">
            {/* Kot */}
            <div className="ods-board-item-token">KOT: {item?.token}</div>

            {/* Delivery */}
            {item?.subOrderType && (
              <div
                className="ods-board-sub-Order-type"
                style={{
                  color: showSubOrderType?.color,
                  backgroundColor: showSubOrderType?.backgroundColor,
                  border: `1px solid ${showSubOrderType?.borderColor}`,
                }}
              >
                {showSubOrderType?.label}
              </div>
            )}
          </div>
        </div>
      </div>

      {stateDifference && retailType === "RESTAURANT" && (
        <div className="ods-board-state-diff">
          <AccessTimeFilledIcon sx={{ fontSize: "13px" }} />
          <span>{stateDifference}</span>
        </div>
      )}

      <div className="ods-board-item-card-customer-container">
        {/* Customer Name */}
        <div className="ods-board-item-customer-name-box">
          {item?.customerName && (
            <div className="ods-board-item-avatar">
              <p>{item?.customerName[0]}</p>
            </div>
          )}
          <div className="ods-board-item-customer-container">
            <p className="ods-board-item-customer-name">{item?.customerName}</p>
            <p className="ods-board-item-customer-number">{item?.mobileNumber}</p>
          </div>
        </div>

        <div className="ods-board-sales-container">
          {/* Sales Channel */}
          {item?.salesChannel && (
            <div className="ods-board-item-channel-wrapper">
              <div
                className="ods-board-item-source-icon"
                style={{
                  color: salesChannelInfo?.color,
                  backgroundColor: salesChannelInfo?.backgroundColor,
                }}
              >
                <p>{salesChannelInfo?.label[0]}</p>
              </div>
              <p>{salesChannelInfo?.label}</p>
            </div>
          )}

          {/* Line Item */}
          <div className="ods-board-line-item-container" onClick={() => handleItem(item)}>
            <p className="ods-board-item-number">
              {item?.numberOfLineItems} {item?.numberOfLineItems === 1 ? "item" : "items"}
            </p>
            <PosCustomIcon
              icon={<ChevronRightIcon />}
              color="#96a2b4"
              height={"11px"}
              width={"11px"}
            />
          </div>
        </div>

        {item?.grandTotal && (
          <div className="ods-board-esitmated-value">
            <p>Estimated Value</p>
            <p className="ods-estimated-price">
              {getStoreCurrencySymbol()}{item?.grandTotal}
              {item?.paymentStatus === "COMPLETED" && (
                <span>
                  <PosCustomIcon
                    icon={<CheckCircleIcon />}
                    height={"16px"}
                    width={"16px"}
                    color="#34C759"
                  />
                </span>
              )}
            </p>
          </div>
        )}
      </div>

      {item?.fulfilmentStatus !== "CREATED" && (
        <div className="ods-board-rider-status">
          <div className="ods-rider-upper-section">
            <div className="ods-board-agent-service">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-bike h-3 w-3 text-slate-500"
                aria-hidden="true"
              >
                <circle cx="18.5" cy="17.5" r="3.5"></circle>
                <circle cx="5.5" cy="17.5" r="3.5"></circle>
                <circle cx="15" cy="5" r="1"></circle>
                <path d="M12 17.5V14l-3-3 4-3 2 3h2"></path>
              </svg>
              <p>{captializeFirstLetter(item?.riderChannel)}</p>
            </div>
            <p
              className="ods-rider-status"
              style={{
                color: riderStatusInfo?.color,
                backgroundColor: riderStatusInfo?.backgroundColor,
              }}
            >
              {riderStatusInfo?.label}
            </p>
          </div>

          <div className="ods-rider-bottom-section">
            <p>OTP</p>
            <p className="ods-board-otp">{item?.riderOtp || "N/A"}</p>
          </div>
        </div>
      )}

      {retailType === "RESTAURANT" && item?.fulfilmentStatus === "CREATED" && (
        <div className="ods-cancel-order-container">
          <PosCustomButton
            title="Accept Order"
            variant="blue-d"
            p="8px 12px"
            width="100%"
            radius="50px"
            onClickFunction={onAcceptOrder ? () => onAcceptOrder(item) : undefined}
          />
        </div>
      )}

      <div className="ods_card_arrow_container">
        {isShowLogisticButton && (
          <PosCustomButton
            title="Logistic Assignment"
            variant="custom-variant"
            customColor="#1e40af"
            customBackgroundColor="#eff6ff"
            customBorder="1px solid #bfdbfe"
            customHover={{background: "#dbeafe"}}
            p="8px 12px"
            width="100%"
            radius="50px"
            onClickFunction={handleLogisticsAssignment}
          />
        )}
        {/* <div className="card__arrow" onClick={() => handleItem(item)}>
          {ArrowIcon}
        </div> */}
      </div>

      <LogisticsModal
        isLogisticsModalOpen={isLogisticsModalOpen}
        setIsLogisticsModalOpen={setIsLogisticsModalOpen}
        item={item}
      />
      <TableConfirmationModal
        tableConfirmationModalOpen={tableConfirmationModalOpen}
        setTableConfirmationModalOpen={setTableConfirmationModalOpen}
        text={confirmationText}
        confirmationButtonText="Yes Reject Order"
        onConfirm={handleConfirmReject}
      />
      <LabelPrintModal
        open={isLabelModalOpen}
        onClose={() => setIsLabelModalOpen(false)}
        order={item}
        items={labelItems}
        onPrint={handleLabelPrint}
        isPrinting={isPrintingLabels}
      />
    </div>
  );
};

export default BoardItem;
